// Three buses, three sliders, three persisted values (SPEC §16).
//
// "Separate volume sliders for music, effects, and voice, all persisted, all
// defaulting to modest. Someone who mutes music entirely should still get the
// announcer, because the announcer carries information."
//
// That last clause is the entire reason this is three gain nodes and not one
// master with a mute. A single mute is the obvious implementation and it silences
// the one channel §16 says must survive — and it fails silently, because a muted
// announcer looks exactly like an announcer with nothing to say.

import type { AudioNodeLike, GainLike, SynthContext } from './graph';

export const BusMusic = 'music';
export const BusEffects = 'effects';
export const BusVoice = 'voice';
export type Bus = typeof BusMusic | typeof BusEffects | typeof BusVoice;

export const Buses: readonly Bus[] = [BusMusic, BusEffects, BusVoice];

/**
 * "Defaulting to modest."
 *
 * Voice sits highest because it carries information the other two do not, and
 * music sits lowest because it is a bed the player did not ask for on a tab they
 * are meant to leave open for four weeks.
 */
export const DefaultVolumes: Readonly<Record<Bus, number>> = {
  music: 0.3,
  effects: 0.5,
  voice: 0.75,
};

export const VolumeKey = 'wwp.volume';

export interface VolumeStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Reads the persisted volumes, falling back per-bus rather than all-or-nothing. */
export function loadVolumes(store?: VolumeStore | null): Record<Bus, number> {
  const out: Record<Bus, number> = { ...DefaultVolumes };
  if (!store) return out;
  let text: string | null = null;
  try {
    text = store.getItem(VolumeKey);
  } catch {
    return out;
  }
  if (!text) return out;
  try {
    const parsed = JSON.parse(text) as Partial<Record<Bus, unknown>>;
    for (const bus of Buses) {
      const v = parsed[bus];
      // Per-bus rather than per-blob: a corrupted "music" must not silently reset
      // the announcer, which is the one the player is least likely to notice.
      if (typeof v === 'number' && Number.isFinite(v)) out[bus] = clamp01(v);
    }
  } catch {
    return out;
  }
  return out;
}

export function saveVolumes(store: VolumeStore | null | undefined, v: Record<Bus, number>): void {
  if (!store) return;
  try {
    store.setItem(VolumeKey, JSON.stringify(v));
  } catch {
    // Storage denied in a private window is not a reason to break the game.
  }
}

/**
 * The bus graph: three gains into one output.
 *
 * There is no master gain. Adding one would give the next person a place to put a
 * global mute, which is the bug this class exists to make impossible.
 */
export class Mixer {
  private readonly gains: Record<Bus, GainLike>;
  private readonly levels: Record<Bus, number>;
  private readonly store: VolumeStore | null;

  constructor(ctx: SynthContext, out: AudioNodeLike, store?: VolumeStore | null) {
    this.store = store ?? null;
    this.levels = loadVolumes(this.store);
    const make = (bus: Bus): GainLike => {
      const g = ctx.createGain();
      g.gain.setValueAtTime(this.levels[bus], ctx.currentTime);
      g.connect(out);
      return g;
    };
    this.gains = { music: make(BusMusic), effects: make(BusEffects), voice: make(BusVoice) };
  }

  /** The node a source for this bus should connect to. */
  bus(b: Bus): GainLike {
    return this.gains[b];
  }

  volume(b: Bus): number {
    return this.levels[b];
  }

  volumes(): Record<Bus, number> {
    return { ...this.levels };
  }

  /**
   * Sets one bus. Ramped rather than stepped: a gain jump on a playing bed is an
   * audible click, and the slider is a drag, so the jump would fire every frame.
   */
  setVolume(b: Bus, v: number, ctx?: SynthContext): void {
    const level = clamp01(v);
    this.levels[b] = level;
    const g = this.gains[b].gain;
    if (ctx) g.linearRampToValueAtTime(level, ctx.currentTime + 0.03);
    else g.value = level;
    saveVolumes(this.store, this.levels);
  }
}
